// Royal Kalahari brand elements (BD-79, CI manual D1.1). The artwork is the designer's master vectors in brand/svg,
// copied here by tools/brand/build.mjs with their shapes unchanged: never redrawn, stretched or typed. Every logo
// says which background it sits on (`on`); its colour then follows the theme (Onyx in light, Sand in dark), and
// the unit test brand-logo-combinations checks every such pair against the manual's p3 list.
import type { CSSProperties, ReactNode } from "react";
import rkIcon from "./rk-icon.svg?raw";
import stackedSlogan from "./logo-stacked-slogan.svg?raw";
import horizontal from "./logo-horizontal-slogan.svg?raw";
import natureScene from "./nature-scene.svg?raw";
import facetTile from "./diamond-facet-tile.svg?url";
import { brand } from "@rk/shared";

/** The backgrounds a logo may sit on in the app: the page, a card, or a header band (theme tokens). */
export type LogoBackground = "background" | "surface" | "band";

type ArtProps = { on: LogoBackground; className?: string; label?: string };

function Art({ svg, on, className, label }: ArtProps & { svg: string }) {
  return (
    <span role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true} data-brand-on={on}
      className={`inline-block text-logo [&>svg]:block [&>svg]:h-full [&>svg]:w-full ${className ?? ""}`}
      dangerouslySetInnerHTML={{ __html: svg }} />
  );
}

/** The RK logo icon (manual p4): the app mark, in headers and on loading screens. */
export const RkIcon = (p: ArtProps) => <Art svg={rkIcon} {...p} />;
/** The stacked logo with the slogan (manual p4): sign-in and first-run screens. */
export const StackedLogo = (p: ArtProps) => <Art svg={stackedSlogan} label={p.label ?? `${brand.name}. ${brand.slogan}`} {...p} />;
/** The horizontal logo with slogan (manual p4; the master file): the desktop sidebar. */
export const HorizontalLogo = (p: ArtProps) => <Art svg={horizontal} label={p.label ?? `${brand.name}. ${brand.slogan}`} {...p} />;

/** The nature scene with gemsbok and acacia (manual p6), in Dune: decoration only, never behind text. */
export function NatureScene({ className }: { className?: string }) {
  return <span aria-hidden className={`inline-block text-accent [&>svg]:block [&>svg]:h-full [&>svg]:w-full ${className ?? ""}`}
    dangerouslySetInnerHTML={{ __html: natureScene }} />;
}

/**
 * The diamond-facet pattern (manual p7) as a faint texture, 5 to 8% opacity, for the sign-in screen and header bands
 * only: never behind data or field-entry text. Place it inside a `relative` container, behind the content.
 */
export function FacetTexture({ opacity = 0.07 }: { opacity?: number }) {
  const style: CSSProperties = { backgroundImage: `url(${facetTile})`, backgroundSize: "118px 101px", opacity };
  return <span aria-hidden data-facet-texture className="pointer-events-none absolute inset-0" style={style} />;
}

/**
 * The sign-in, Face ID and first-run screens (BD-79): the stacked logo with the slogan on a faint facet band at the
 * top, the content, and the Dune nature scene along the bottom, kept clear of the content (never behind the form).
 */
export function BrandScreen({ children, heading = true, testid }: { children: ReactNode; heading?: boolean; testid?: string }) {
  return (
    <main data-testid={testid} className="relative min-h-dvh overflow-hidden bg-bg">
      <div className="relative mx-auto flex min-h-dvh max-w-md flex-col px-6 pb-[min(44vw,300px)] pt-8 safe-top">
        <div className="relative mb-8 flex justify-center overflow-hidden rounded-card py-6">
          <FacetTexture />
          <StackedLogo on="background" className="relative h-[150px] w-[170px]" />
        </div>
        {heading && <h1 className="sr-only">{brand.name}</h1>}
        {children}
      </div>
      <NatureScene className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto aspect-[855/296] w-full max-w-[760px]" />
    </main>
  );
}

/** While the app starts (BD-79): the RK icon centred on the page, with a short line. */
export function BrandLoading({ label = "Getting things ready" }: { label?: string }) {
  return (
    <main role="status" aria-live="polite" className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center">
      <RkIcon on="background" label={brand.name} className="h-16 w-[102px] animate-pulse motion-reduce:animate-none" />
      <p className="text-[17px] text-muted">{label}</p>
    </main>
  );
}
